import { NavigationGuard } from 'vue-router';
import store from '@/store';
import trackSeekerAccountCreation from '@/lib/tracking/segment_track_basic';
import { establishAuthenticatedSession } from '@/lib/establishAuthenticatedSession';
import { getSharedApolloClient } from '@/lib/apollo_setup';
import { fetchActiveScCareSessions } from '@/helpers/scCareSessionHelper';
import { isOn, syncGrowthbookUserAttributes } from '@/lib/growthbook';
import type { Account } from '@/models/account';

const SESSION_STORAGE_KEY = 'corporate_onboarding_segment_events_fired';
const CORPORATE_ONBOARDING_NEW_FLAG = 'corporate-onboarding-new';
const GUIDED_PATH = '/corporate-onboarding/guided';
const CARE_TYPE_PATH = '/corporate-onboarding/care-type';
const BACKUP_CARE_PATH = '/corporate-onboarding/backup-care';

function isCorporateOnboardingIndex(path: string): boolean {
  return path === '/corporate-onboarding' || path === '/corporate-onboarding/';
}

function isGuidedRoute(path: string): boolean {
  return path === GUIDED_PATH || path.startsWith(`${GUIDED_PATH}/`);
}

/** Sync JWT attributes then evaluate — SPA auth can leave GrowthBook with a null userId. */
async function isCorporateOnboardingNewEnabled(): Promise<boolean> {
  await syncGrowthbookUserAttributes();
  return isOn(CORPORATE_ONBOARDING_NEW_FLAG);
}

/**
 * Ensures Segment identify + corporate onboarding account-creation tracks via the auth-handoff
 * helper before guided/care-type routing continues. Not used for global route bootstrap.
 */
export async function trackCorporateOnboardingSegment(): Promise<void> {
  try {
    await establishAuthenticatedSession({
      store,
      tracks: async (account: Account) => {
        if (sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true') {
          return;
        }

        trackSeekerAccountCreation({
          firstName: account.firstName,
          lastName: account.lastName,
          email: account.email,
          familyType: account.accountType(),
        });
        sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
      },
    });
  } catch (error) {
    console.error('Error in trackCorporateOnboardingSegment:', error);
  }
}

/**
 * Resolves corporate onboarding entry. BUC users (active SC care sessions) always get backup-care
 * and never enter the guided experiment — including when external signup lands on the index path.
 * Non-BUC users follow the corporate-onboarding-new experiment (guided) or control (care-type).
 *
 * GrowthBook isOn must not run until BUC eligibility is known: evaluation fires Experiment Viewed.
 */
export const resolveCorporateOnboardingFlow: NavigationGuard = async (to, _from, next) => {
  const isIndexRoute = isCorporateOnboardingIndex(to.path);
  const isCareTypeRoute = to.path === CARE_TYPE_PATH;
  const isBackupCareRoute = to.path === BACKUP_CARE_PATH;
  const isGuidedOnboardingRoute = isGuidedRoute(to.path);

  if (!isIndexRoute && !isCareTypeRoute && !isBackupCareRoute && !isGuidedOnboardingRoute) {
    next();
    return;
  }

  // Session lookup already failed; these users are the experiment audience.
  if (to.query.skip_backup_care === 'true') {
    const isNewOnboardingEnabled = await isCorporateOnboardingNewEnabled();

    if (isGuidedOnboardingRoute) {
      if (isNewOnboardingEnabled) {
        next();
        return;
      }
      next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
      return;
    }

    if (isIndexRoute) {
      next({
        path: isNewOnboardingEnabled ? GUIDED_PATH : CARE_TYPE_PATH,
        query: to.query,
        replace: true,
      });
      return;
    }

    if (isBackupCareRoute) {
      next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
      return;
    }

    next();
    return;
  }

  try {
    const apolloClient = getSharedApolloClient();
    const activeSessions = await fetchActiveScCareSessions(apolloClient);
    const hasActiveBackupCare = activeSessions.length > 0;

    // BUC always wins: never evaluate the experiment
    if (hasActiveBackupCare) {
      if (isBackupCareRoute) {
        next();
        return;
      }

      next({ path: BACKUP_CARE_PATH, query: to.query, replace: true });
      return;
    }

    const isNewOnboardingEnabled = await isCorporateOnboardingNewEnabled();

    if (isNewOnboardingEnabled) {
      if (isIndexRoute) {
        next({ path: GUIDED_PATH, query: to.query, replace: true });
        return;
      }

      if (isBackupCareRoute) {
        next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
        return;
      }

      // Care-type and guided remain reachable for non-BUC experiment users.
      next();
      return;
    }

    if (isGuidedOnboardingRoute || isIndexRoute || isBackupCareRoute) {
      next({ path: CARE_TYPE_PATH, query: to.query, replace: true });
      return;
    }

    next();
  } catch (error) {
    console.error('Error resolving corporate onboarding flow:', error);

    if (isIndexRoute || isBackupCareRoute || isGuidedOnboardingRoute) {
      next({
        path: CARE_TYPE_PATH,
        query: { ...to.query, skip_backup_care: 'true' },
        replace: true,
      });
      return;
    }

    next();
  }
};

const corporateOnboardingGuard: NavigationGuard = async (to, from, next) => {
  await trackCorporateOnboardingSegment();
  await resolveCorporateOnboardingFlow(to, from, next);
};

export default corporateOnboardingGuard;
